// frontend/src/components/OptionLogicEditor.tsx
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Settings2 } from "lucide-react";
import { QuestionType, OptionLogic, SkipAction, TerminationType, OTHER_OPTION_ID } from "@/types/survey";

interface OptionLogicEditorProps {
  question: QuestionType;
  allQuestions: QuestionType[];
  onChange: (optionLogic: OptionLogic[]) => void;
  onRandomizeOptionsChange?: (randomize: boolean) => void;
}

export function OptionLogicEditor({ question, allQuestions, onChange, onRandomizeOptionsChange }: OptionLogicEditorProps) {
  const options = question.options || [];
  const [optionLogic, setOptionLogic] = useState<OptionLogic[]>(
    question.optionLogic || options.map((opt, i) => ({
      optionId: opt.id,
      optionValue: opt.text,
      optionCode: String(i + 1),
    }))
  );
  const [_expandedOption, _setExpandedOption] = useState<string | null>(null);
  
  // Helper to find logic by option ID
  const getLogicForOption = (optionId: string): OptionLogic | undefined => {
    return optionLogic.find(ol => ol.optionId === optionId);
  };
  
  // Helper to update logic by option ID
  const updateOptionLogicById = (optionId: string, updates: Partial<OptionLogic>) => {
    const existingIndex = optionLogic.findIndex(ol => ol.optionId === optionId);
    const newLogic = [...optionLogic];
    
    if (existingIndex >= 0) {
      newLogic[existingIndex] = { ...newLogic[existingIndex], ...updates };
    } else {
      // Special case: "Other" is a virtual option not in the options array
      if (optionId === OTHER_OPTION_ID) {
        newLogic.push({
          optionId,
          optionValue: question.otherLabel || "Other (please specify)",
          optionCode: "OTHER",  // Stable code for "Other"
          ...updates,
        });
      } else {
        const option = options.find(o => o.id === optionId);
        newLogic.push({
          optionId,
          optionValue: option?.text || '',
          optionCode: String(options.findIndex(o => o.id === optionId) + 1),
          ...updates,
        });
      }
    }
    
    setOptionLogic(newLogic);
    onChange(newLogic);
  };
  
  const setOptionAction = (optionId: string, action: SkipAction | null) => {
    if (action === null) {
      // Remove skip logic
      updateOptionLogicById(optionId, { skipLogic: undefined });
    } else {
      updateOptionLogicById(optionId, {
        skipLogic: {
          conditions: [],
          conditionOperator: "AND",
          action,
          terminationType: action === "terminate" ? "screen_out" : undefined,
        },
      });
    }
  };
  
  // Get questions that come after this one
  const futureQuestions = allQuestions.filter((q, i) => {
    const currentIndex = allQuestions.findIndex(aq => aq.id === question.id);
    return i > currentIndex;
  });
  
  // Helper to truncate text
  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Render option logic target (skip to question or termination type)
  const renderOptionLogicTarget = (optionId: string) => {
    const logic = getLogicForOption(optionId);
    
    if (logic?.skipLogic?.action === "skip_to_question") {
      return (
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">
            Skip to question
          </Label>
          <Select
            value={logic.skipLogic.targetQuestionId || ""}
            onValueChange={(v) => {
              const target = allQuestions.find(q => q.id === v);
              const currentSkipLogic = logic.skipLogic || { conditions: [], conditionOperator: "AND" as const, action: "skip_to_question" as const };
              updateOptionLogicById(optionId, {
                skipLogic: {
                  ...currentSkipLogic,
                  targetQuestionId: v,
                  targetOriginalNumber: target?.originalQuestionNumber,
                },
              });
            }}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Select target question" />
            </SelectTrigger>
            <SelectContent>
              {futureQuestions.map((q) => (
                <SelectItem key={q.id} value={q.id}>
                  {q.originalQuestionNumber || `Q${allQuestions.findIndex(aq => aq.id === q.id) + 1}`}: {truncate(q.question, 40)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    if (logic?.skipLogic?.action === "terminate") {
      return (
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">
            Termination type
          </Label>
          <Select
            value={logic.skipLogic.terminationType || "screen_out"}
            onValueChange={(v) => {
              const currentSkipLogic = logic.skipLogic || { conditions: [], conditionOperator: "AND" as const, action: "terminate" as const };
              updateOptionLogicById(optionId, {
                skipLogic: {
                  ...currentSkipLogic,
                  terminationType: v as TerminationType,
                },
              });
            }}
          >
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="screen_out">Screen Out</SelectItem>
              <SelectItem value="thank_and_close">Thank & Close</SelectItem>
              <SelectItem value="quota_full">Quota Full</SelectItem>
              <SelectItem value="disqualified">Disqualified</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    return null;
  };

  // Render "Other" option settings
  const renderOtherOptionSettings = () => {
    const otherLogic = getLogicForOption(OTHER_OPTION_ID);
    
    return (
      <>
        {/* Option Code */}
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">
            Option Code (for export)
          </Label>
          <Input
            value={otherLogic?.optionCode || "OTHER"}
            onChange={(e) => updateOptionLogicById(OTHER_OPTION_ID, { optionCode: e.target.value })}
            placeholder="OTHER"
            className="h-8 font-mono text-sm"
          />
        </div>
        
        {/* Skip Action */}
        <div className="grid gap-2">
          <Label className="text-xs text-muted-foreground">
            When selected, then...
          </Label>
          <Select
            value={otherLogic?.skipLogic?.action || "none"}
            onValueChange={(v) => setOptionAction(OTHER_OPTION_ID, v === "none" ? null : v as SkipAction)}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Continue normally" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Continue normally</SelectItem>
              <SelectItem value="continue" className="text-green-600 dark:text-green-400">
                Continue to next
              </SelectItem>
              <SelectItem value="skip_to_question" className="text-blue-600 dark:text-blue-400">
                Skip to question
              </SelectItem>
              <SelectItem value="terminate" className="text-destructive">
                Terminate survey
              </SelectItem>
              <SelectItem value="skip_to_end" className="text-orange-600 dark:text-orange-400">
                Skip to end
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Conditional Target/Termination Type */}
        {renderOptionLogicTarget(OTHER_OPTION_ID)}
      </>
    );
  };

  if (options.length === 0 && !question.allowOther) {
    return (
      <p className="text-sm text-muted-foreground italic">
        Add options to this question to configure option-level logic.
      </p>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Randomize Options Toggle */}
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Randomize option order</span>
        </div>
        <Switch
          checked={question.randomizeOptions || false}
          onCheckedChange={(checked) => {
            onRandomizeOptionsChange?.(checked);
          }}
        />
      </div>
      
      {/* Per-option settings */}
      <div className="space-y-2">
        {options.map((option, idx) => {
          const logic = getLogicForOption(option.id);
          
          return (
            <Collapsible key={option.id}>
              <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                <span className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full flex items-center justify-center text-sm font-medium shrink-0">
                  {logic?.optionCode || idx + 1}
                </span>
                <span className="text-sm flex-1 text-left">
                  {option.text || `Option ${idx + 1}`}
                </span>
                {logic?.skipLogic && (
                  <span className={`text-xs px-2 py-1 rounded ${
                    logic.skipLogic.action === "terminate" ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300" :
                    logic.skipLogic.action === "skip_to_question" ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" :
                    logic.skipLogic.action === "skip_to_end" ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300" :
                    "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                  }`}>
                    {logic.skipLogic.action === "terminate" ? "Terminates" :
                     logic.skipLogic.action === "skip_to_question" ? `→ ${logic.skipLogic.targetOriginalNumber || "Q?"}` :
                     logic.skipLogic.action === "skip_to_end" ? "Skip to end" :
                     "Continues"}
                  </span>
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 pb-3 px-3 space-y-3">
                {/* Option Code */}
                <div className="grid gap-2">
                  <Label className="text-xs text-muted-foreground">
                    Option Code (for export)
                  </Label>
                  <Input
                    value={logic?.optionCode || String(idx + 1)}
                    onChange={(e) => updateOptionLogicById(option.id, { optionCode: e.target.value })}
                    placeholder={String(idx + 1)}
                    className="h-8 font-mono text-sm"
                  />
                </div>
                
                {/* Skip Action */}
                <div className="grid gap-2">
                  <Label className="text-xs text-muted-foreground">
                    When selected, then...
                  </Label>
                  <Select
                    value={logic?.skipLogic?.action || "none"}
                    onValueChange={(v) => setOptionAction(option.id, v === "none" ? null : v as SkipAction)}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Continue normally" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Continue normally</SelectItem>
                      <SelectItem value="continue" className="text-green-600 dark:text-green-400">
                        Continue to next
                      </SelectItem>
                      <SelectItem value="skip_to_question" className="text-blue-600 dark:text-blue-400">
                        Skip to question
                      </SelectItem>
                      <SelectItem value="terminate" className="text-destructive">
                        Terminate survey
                      </SelectItem>
                      <SelectItem value="skip_to_end" className="text-orange-600 dark:text-orange-400">
                        Skip to end
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Conditional Target/Termination Type */}
                {renderOptionLogicTarget(option.id)}
                
                {/* Option Modifiers - preserved from existing */}
                <div className="flex items-center gap-6 pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`anchor-${option.id}`}
                      checked={logic?.isAnchor || false}
                      onCheckedChange={(checked) => updateOptionLogicById(option.id, { isAnchor: checked })}
                    />
                    <Label htmlFor={`anchor-${option.id}`} className="text-xs">Anchor</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`exclusive-${option.id}`}
                      checked={logic?.isExclusive || false}
                      onCheckedChange={(checked) => updateOptionLogicById(option.id, { isExclusive: checked })}
                    />
                    <Label htmlFor={`exclusive-${option.id}`} className="text-xs">Exclusive</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`mustselect-${option.id}`}
                      checked={logic?.isMustSelect || false}
                      onCheckedChange={(checked) => updateOptionLogicById(option.id, { isMustSelect: checked })}
                    />
                    <Label htmlFor={`mustselect-${option.id}`} className="text-xs">Must Select</Label>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
        
        {/* "Other" Option Logic */}
        {question.allowOther && (() => {
          const otherLogic = getLogicForOption(OTHER_OPTION_ID);
          return (
          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
              <span className="w-8 h-8 bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded-full flex items-center justify-center text-sm font-medium shrink-0">
                ✱
              </span>
              <span className="text-sm flex-1 text-left italic">
                {question.otherLabel || "Other (please specify)"}
              </span>
              {otherLogic?.skipLogic && (
                <span className={`text-xs px-2 py-1 rounded ${
                  otherLogic.skipLogic.action === "terminate" ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300" :
                  otherLogic.skipLogic.action === "skip_to_question" ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" :
                  otherLogic.skipLogic.action === "skip_to_end" ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300" :
                  "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                }`}>
                  {otherLogic.skipLogic.action === "terminate" ? "Terminates" :
                   otherLogic.skipLogic.action === "skip_to_question" ? `→ ${otherLogic.skipLogic.targetOriginalNumber || "Q?"}` :
                   otherLogic.skipLogic.action === "skip_to_end" ? "Skip to end" :
                   "Continues"}
                </span>
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 pb-3 px-3 space-y-3">
              {/* Other option settings */}
              {renderOtherOptionSettings()}
            </CollapsibleContent>
          </Collapsible>
          );
        })()}
      </div>
    </div>
  );
}
